/**
 * `[anudan_payment_update]` — the page WP-27's dunning email links to (WP-31,
 * `Frontend\Portal\PaymentUpdatePageRenderer`'s own docblock).
 *
 * No WordPress session, no nonce — the single-use token in the URL is the whole security
 * boundary (`Rest\SubscriptionPaymentMethodController`'s own docblock), read here only from the
 * `data-anudan-token` attribute the server already resolved, never re-parsed from the query
 * string by this script.
 */

import { loadGateway } from './checkout/gateway-loader.js';
import { plainFailureMessage, reportFailure } from './payment-method-failure.js';
import { readConfig } from './shared/config-block.js';
import { stripeElementsOptions, paymentElementOptions } from './shared/brand.js';
import { whenReady } from './shared/stripe-ready.js';

readConfig( 'anudan-payment-update' );

function restRoot() {
	const config = window.AnudanEngine && window.AnudanEngine.config;

	return ( config && config.restUrl ) || '/wp-json/anudan/v1/';
}

/**
 * @param {string} path
 * @param {object} [body]
 * @return {Promise<{ok: boolean, status: number, body: any}>}
 */
async function post( path, body ) {
	const response = await fetch( restRoot() + path, {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify( body || {} ),
	} );

	let parsed = null;

	try {
		parsed = await response.json();
	} catch ( error ) {
		parsed = null;
	}

	return { ok: response.ok, status: response.status, body: parsed };
}

function t( text ) {
	return window.wp && window.wp.i18n ? window.wp.i18n.__( text, 'anudan' ) : text;
}

/**
 * Hides an element robustly: the native `hidden` attribute alone loses to this component's own
 * `!important` layout rules on `.anudan-portal__filters`/`.anudan-portal__button` — an inline
 * style, which no stylesheet rule here outranks, is what actually hides it.
 *
 * @param {HTMLElement} el
 */
function hide( el ) {
	el.hidden = true;
	el.style.setProperty( 'display', 'none', 'important' );
}

async function initPaymentUpdate( root ) {
	const token = root.dataset.anudanToken;
	const statusRegion = root.querySelector( '[data-anudan-payment-update-status]' );
	const errorRegion = root.querySelector( '[data-anudan-payment-update-error]' );
	const mountPoint = root.querySelector( '[data-anudan-payment-element]' );
	const submitButton = root.querySelector( '[data-anudan-payment-update-submit]' );

	// Not pressable until Stripe's card form is ready (`shared/stripe-ready.js`).
	if ( submitButton ) {
		submitButton.disabled = true;
	}

	function showError( message ) {
		if ( errorRegion ) {
			errorRegion.textContent = message;
			errorRegion.hidden = false;
		}
	}

	const intentResponse = await post( `subscriptions/payment-method/${ encodeURIComponent( token ) }/setup-intent`, {} );

	if ( ! intentResponse.ok || ! intentResponse.body ) {
		showError( ( intentResponse.body && intentResponse.body.message ) || t( 'This link is no longer valid.' ) );
		hide( submitButton );

		return;
	}

	const gatewayGlobal = await loadGateway( intentResponse.body.gateway );

	if ( ! gatewayGlobal ) {
		showError( t( 'We could not load the payment form. Please refresh and try again.' ) );
		hide( submitButton );

		return;
	}

	const publishableKey = intentResponse.body.public_config && intentResponse.body.public_config.publishable_key;
	const stripe = gatewayGlobal( publishableKey );
	const elements = stripe.elements( stripeElementsOptions( { clientSecret: intentResponse.body.client_token } ) );
	const paymentElement = elements.create( 'payment', paymentElementOptions() );

	paymentElement.mount( mountPoint );
	whenReady( paymentElement, submitButton, root, () => {
		showError( t( 'We could not load the payment form. Please refresh and try again.' ) );
		hide( submitButton );
	} );

	function setStatus( message ) {
		if ( statusRegion ) {
			statusRegion.textContent = message;
		}
	}

	/*
	 * 0.9.41-rc5: the save says it is working, and a step that throws (the network dropped, Stripe's script failed) ends in a plain
	 * message with Save card pressable again. Before, a thrown error ended the handler silently, the button stayed disabled, and the
	 * donor saw nothing at all: suspected to be the one save local:e2e saw get no answer within 60 s on rc4, not reproduced.
	 */
	async function save() {
		const confirmation = await stripe.confirmSetup( { elements, redirect: 'if_required' } );

		if ( confirmation.error ) {
			submitButton.disabled = false;
			setStatus( '' );
			showError( plainFailureMessage( confirmation.error ) );
			reportFailure( restRoot(), `subscriptions/payment-method/${ encodeURIComponent( token ) }/failed` );

			return;
		}

		const paymentMethodId = confirmation.setupIntent && confirmation.setupIntent.payment_method;

		const updateResponse = await post(
			`subscriptions/payment-method/${ encodeURIComponent( token ) }`,
			{ payment_method_id: paymentMethodId }
		);

		if ( ! updateResponse.ok ) {
			submitButton.disabled = false;
			setStatus( '' );
			showError( ( updateResponse.body && updateResponse.body.message ) || t( 'We could not save this card.' ) );

			return;
		}

		mountPoint.hidden = true;
		hide( submitButton );
		setStatus( t( 'Your payment method has been updated.' ) );
	}

	submitButton.addEventListener( 'click', async () => {
		submitButton.disabled = true;

		if ( errorRegion ) {
			errorRegion.hidden = true;
		}

		setStatus( t( 'Saving your card…' ) );

		try {
			await save();
		} catch ( error ) {
			submitButton.disabled = false;
			setStatus( '' );
			showError( t( 'We could not save this card. Please check your connection and press Save card again.' ) );
		}
	} );
}

function initRequestLinkForm( root ) {
	const form = root.querySelector( '[data-anudan-request-link-form]' );
	const statusRegion = root.querySelector( '[data-anudan-request-link-status]' );

	if ( ! form ) {
		return;
	}

	form.addEventListener( 'submit', async ( event ) => {
		event.preventDefault();

		const email = String( new FormData( form ).get( 'email' ) || '' );

		const response = await post( 'subscriptions/payment-method/request-link', { email } );

		if ( statusRegion ) {
			statusRegion.textContent = ( response.body && response.body.message )
				|| t( 'If that email address has a recurring gift needing attention, we\'ve sent a fresh link.' );
		}

		hide( form );
	} );
}

document.querySelectorAll( '[data-anudan-payment-update]' ).forEach( ( root ) => {
	initPaymentUpdate( root );
} );

document.querySelectorAll( '[data-anudan-payment-update-invalid]' ).forEach( ( root ) => {
	initRequestLinkForm( root );
} );
